import React, { useEffect, useState, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Divider,
  Table,
  Tooltip,
} from 'antd';
import { SwapLeftOutlined, SwapRightOutlined } from '@ant-design/icons';
import { toISTDate } from '@Apis/constants';
import H3Text from '@Uilib/h3Text';
import PurchaseRequestActions from '@Actions/purchaseRequestActions';
import UserActions from '@Actions/userActions';
import H3Modal from '@Uilib/H3Modal';
import ViewIndent from '../../../Inventory/Indent/ViewIndent';
import PRZText from '../../../Common/UI/PRZText';
import useCheckPortrait from '../../../../hooks/useCheckPortrait';
import './style.scss';

const PendingStockAdjustmentList = ({
  user,
  getPendingActions,
  getPendingActionsLoading,
  pendingActions,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showSoModal, setShowSoModal] = useState(false);
  const [selectedStockAdjustment, setSelectedStockAdjustment] = useState(null);

  const {
    isMobile, isPortrait, isMobileAndPortrait, isMobileOrPortrait,
  } = useCheckPortrait();

  const tableColumns = [
    {
      title: '',
      render: (record) => (
        <div className="mobile-list__card">
          <div className="mobile-list__card_header">
            <div className="mobile-list__card_header-left">
              <PRZText text={`#${record?.ia_number}`} className="mobile-list__card_item-number" />
            </div>
          </div>
          <div className="mobile-list__card_body">
            <div className="mobile-list__card_body-left" style={{ width: '100%' }}>
              <PRZText
                text={`Business Unit - ${record?.tenant_info?.tenant_name} (${record?.alias_name})`}
                className="mobile-list__card_item-details"
              />
              <PRZText
                text={`Items(s) - ${record?.no_of_items}`}
                className="mobile-list__card_item-details"
              />
              <Tooltip
                title={(
                  <PRZText
                    className="mobile-list__card_item__tooltip_text-color mobile-list__card_item__category__tooltip"
                    text={`${record?.adjustment_reason?.replaceAll('_', ' ')?.toProperCase()} (${record?.remarks ? record?.remarks : ''})`}
                  />
                )}
                color="white"
              >
                <PRZText text={`Adjustment Reason - ${record?.adjustment_reason?.replaceAll('_', ' ')?.toProperCase()} (${record?.remarks ? record?.remarks : ''})`} className="mobile-list__card_item-details" />
              </Tooltip>

              <Tooltip
                title={(
                  <PRZText
                    className="mobile-list__card_item__tooltip_text-color mobile-list__card_item__category__tooltip"
                    text={`${toISTDate(record?.order_date).format('DD/MM/YYYY')} ( ${record?.created_by_info ? `${record?.created_by_info.first_name} ${record?.created_by_info.last_name}` : 'Unknown'} )`}
                  />
                )}
                color="white"
              >
                <PRZText text={`Created By ${record?.created_by_info ? `${record?.created_by_info.first_name} ${record?.created_by_info.last_name}` : 'Unknown'} on ${toISTDate(record?.order_date).format('DD/MM/YYYY')}`} className="mobile-list__card_item-details created_by_info" />
              </Tooltip>
            </div>
          </div>
          <Divider className="mobile-list__card_divider" />
          <div className="mobile-list__card_footer">
            <div className="mobile-list__card_footer-left">
              {record?.adjustment_entity_type?.replaceAll('_', ' ') === 'OUTWARD'
              && (
                <div className="ia-type-tag" style={{ backgroundColor: '#f15353', marginTop: '5px' }}>
                  <SwapRightOutlined />
                  <H3Text text="Outgoing" className="" />
                </div>
              )}
              {record?.adjustment_entity_type?.replaceAll('_', ' ') === 'INWARDS'
              && (
                <div className="ia-type-tag" style={{ backgroundColor: '#2fbc6e', marginTop: '5px' }}>
                  <SwapLeftOutlined />
                  <H3Text text="Incoming" className="" />
                </div>
              )}
            </div>
          </div>
        </div>
      ),
      // responsive: ['xs'],
      hidden: !isMobile,
    },
    {
      title: 'ADJUSTMENT #',
      render: (text, record) => (
        <div className="column-number__wrapper">
          <H3Text text={record?.ia_number} />
        </div>
      ),
      responsive: ['sm', 'md', 'lg', 'xxl'],
      hidden: isMobile,
    },
    {
      title: 'BUSINESS UNIT',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      hidden: isMobile,
      render: (text, record) => (
        <div>
          <H3Text text={record?.tenant_info?.tenant_name} />
          <H3Text text={record?.alias_name} className="table-subscript" />
        </div>
      ),
    },
    {
      title: 'ADJUSTMENT TYPE',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      hidden: isMobile,
      render: (text, record) => (
        <Fragment>
          {record?.adjustment_entity_type?.replaceAll('_', ' ') === 'OUTWARD'
                && (
                  <div className="ia-type-tag" style={{ backgroundColor: '#f15353' }}>
                    <SwapRightOutlined />
                    <H3Text text="Outgoing" className="" />
                  </div>
                )}
          {record?.adjustment_entity_type?.replaceAll('_', ' ') === 'INWARDS'
                && (
                  <div className="ia-type-tag" style={{ backgroundColor: '#2fbc6e' }}>
                    <SwapLeftOutlined />
                    <H3Text text="Incoming" className="" />
                  </div>
                )}
        </Fragment>
      ),
    },
    {
      title: 'ADJUSTMENT REASON',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      hidden: isMobile,
      render: (text, record) => (
        <div>
          {record?.adjustment_reason?.replaceAll('_', ' ')?.toProperCase()}
          {record?.remarks ? <H3Text text={record?.remarks} className="table-subscript" /> : ''}
        </div>
      ),
    },
    {
      title: 'ITEM(s)',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      hidden: isMobile,
      render: (text, record) => (
        <div>
          {record?.no_of_items}
        </div>
      ),
    },
    {
      title: 'CREATED DATE',
      render: (record) => (
        <div>
          <div className="column-date">
            {toISTDate(record?.order_date).format('DD/MM/YYYY')}
          </div>
          <H3Text text={(record?.created_by_info ? `${record?.created_by_info.first_name} ${record?.created_by_info.last_name}` : 'Unknown')} className="table-subscript" />
        </div>
      ),
      responsive: ['sm', 'md', 'lg', 'xxl'],
      hidden: isMobile,
    },
    {
      title: 'ADJUSTMENT DATE',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      hidden: isMobile,
      render: (text, record) => (
        <div>
          <div className="column-date">
            {toISTDate(record?.adjustment_date).format('DD/MM/YYYY')}
          </div>
          <H3Text text={(record?.created_by_info ? `${record?.created_by_info.first_name} ${record?.created_by_info.last_name}` : 'Unknown')} className="table-subscript" />
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      fixed: 'right',
      render: (record) => (
        <div className="list__actions">
          <div className="list__actions-button list__actions-button-dark" onClick={() => { setSelectedStockAdjustment(record); setShowSoModal(true); }}>
            View
          </div>
        </div>
      ),
      responsive: ['sm', 'md', 'lg', 'xxl'],
      hidden: isMobile,
      className: 'list__actions-column',
    },
  ].filter((item) => !item.hidden);

  useEffect(() => {
    if (user) {
      getPendingActions('map_sa', 10, currentPage, () => {});
    }
  }, [user, getPendingActions, currentPage]);
  return (
    <React.Fragment>
      <div className="doc-list__wrapper">
        <div className="ant-row">
          <div className="ant-col-md-24 ant-col-xs-24">
            <div className="doc-list__table-wrapper">
              <Table
                title={() => (
                  <div className="doc-list__table-header">
                    <div className="doc-list__table-header__left">
                      {`${Number(pendingActions?.sa_approval_count) || 0} Stock Adjustments`}
                    </div>
                  </div>
                )}
                loading={getPendingActionsLoading}
                showHeader
                size="small"
                scroll="scroll"
                columns={tableColumns}
                rowKey="ia_id"
                dataSource={pendingActions?.stock_adjustments || []}
                pagination={(Number(pendingActions?.sa_approval_count) > 10) ? {
                  defaultCurrent: 1,
                  current: currentPage,
                  total: Number(pendingActions?.sa_approval_count),
                  pageSize: 10,
                  onChange: (page) => {
                    getPendingActions('map_sa', 10, page, () => {});
                    setCurrentPage(page);
                  },
                  position: ['bottomRight'],
                  showSizeChanger: false,
                } : false}
              />
            </div>
          </div>
        </div>
      </div>
      <H3Modal
        onClose={() => {
          getPendingActions('map_sa', 10, currentPage, () => {});
          setShowSoModal(false);
        }}
        className="pending-sa__view-sa"
        isOpen={showSoModal}
        title={`Stock Adjustment #${selectedStockAdjustment ? selectedStockAdjustment?.ia_number : ''} for ${selectedStockAdjustment?.tenant_info?.tenant_name}`}
        showHeader
      >
        <ViewIndent selectedIndentId={selectedStockAdjustment?.ia_id} selectedTenantId={selectedStockAdjustment?.tenant_info?.tenant_id} isQuickView />
      </H3Modal>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  UserReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  getPendingActionsLoading: UserReducers.getPendingActionsLoading,
  pendingActions: UserReducers.pendingActions,
});

const mapDispatchToProps = (dispatch) => ({
  getPendingActions: (actionType, limit, page, callback) => dispatch(UserActions.getPendingActions(actionType, limit, page, callback)),
  getPurchaseRequestsSuccess: (purchaseRequests) => dispatch(PurchaseRequestActions.getPurchaseRequestsSuccess(purchaseRequests)),
});

PendingStockAdjustmentList.propTypes = {
  user: PropTypes.any,
  getPendingActions: PropTypes.func,
  getPendingActionsLoading: PropTypes.bool,
  pendingActions: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(PendingStockAdjustmentList);
